import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Typography,
  Chip,
  InputAdornment,
  TextField,
  Breadcrumbs,
  Link,
  CircularProgress,
  IconButton,
  Avatar,
} from '@mui/material';
import {
  Search as SearchIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Sidebar from '../components/Sidebar';
import { useAuth } from '../contexts/AuthContext';
import Pagination from '../components/common/Pagination';

const VARIANT_CONFIG = {
  retail: {
    clientType: 'retail',
    tagLabel: 'Minorista',
    sectionName: 'Pedidos al público',
    title: 'Clientes Minoristas',
    subtitle: 'Clientes registrados para venta al público.',
  },
  wholesale: {
    clientType: 'wholesale',
    tagLabel: 'Mayorista',
    sectionName: 'Pedidos mayoristas',
    title: 'Clientes Mayoristas',
    subtitle: 'Comercios y clientes mayoristas registrados en el sistema.',
  },
};

const SectionClients = ({ variant = 'retail' }) => {
  const config = VARIANT_CONFIG[variant] || VARIANT_CONFIG.retail;
  const { clientType, tagLabel } = config;

  const navigate = useNavigate();
  const { logout } = useAuth();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  // Pagination
  const [page, setPage] = useState(0);
  const [totalCount, setTotalCount] = useState(0);

  useEffect(() => {
    setPage(0);
    setSearchTerm('');
  }, [variant]);

  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      fetchClients();
    }, 300);
    return () => clearTimeout(delayDebounce);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, searchTerm, variant]);

  const fetchClients = async () => {
    try {
      setLoading(true);
      const params = [
        `page=${page + 1}`,
        'is_active=true',
        `client_type=${clientType}`,
        searchTerm ? `search=${encodeURIComponent(searchTerm)}` : '',
      ].filter(Boolean).join('&');

      const response = await axios.get(`/api/clients/?${params}`);
      if (response.data && response.data.results) {
        setClients(response.data.results);
        setTotalCount(response.data.count);
      } else {
        setClients(response.data || []);
        setTotalCount(response.data ? response.data.length : 0);
      }
    } catch (error) {
      console.error('Error fetching clients:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const getInitials = (name) => {
    if (!name) return 'C';
    const words = name.split(' ');
    if (words.length >= 2) {
      return (words[0][0] + words[1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'aprobado':
        return 'Aprobado';
      case 'revision':
        return 'Revisión';
      case 'bloqueado':
        return 'Bloqueado';
      default:
        return status;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'aprobado':
        return { bg: '#e8f5e9', color: '#2e7d32', border: '#a5d6a7' };
      case 'revision':
        return { bg: '#fff3e0', color: '#ff9800', border: '#ffe0b2' };
      case 'bloqueado':
        return { bg: '#ffeedd', color: '#d32f2f', border: '#ffcdd2' };
      default:
        return { bg: '#f5f5f5', color: '#9e9e9e', border: '#e0e0e0' };
    }
  };

  const getPriceListLabel = (priceList) => {
    const labels = {
      mayorista_a: 'Mayorista A',
      mayorista_b: 'Mayorista B',
      premium: 'Premium',
      estandar: 'Estándar',
    };
    return labels[priceList] || priceList;
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, minHeight: '100vh', bgcolor: '#f6f7f8' }}>
      <Sidebar onLogout={handleLogout} />
      <Box sx={{ flex: 1, p: { xs: 2, md: 4 }, pt: { xs: 7, md: 4 } }}>
        {/* Breadcrumbs */}
        <Breadcrumbs sx={{ mb: 3 }}>
          <Link
            color="inherit"
            href="#"
            onClick={(e) => { e.preventDefault(); navigate('/'); }}
            sx={{ color: '#4e7397', fontSize: '0.875rem' }}
          >
            Inicio
          </Link>
          <Typography sx={{ color: '#4e7397', fontSize: '0.875rem' }}>{config.sectionName}</Typography>
          <Typography sx={{ color: '#0e141b', fontSize: '0.875rem', fontWeight: 500 }}>Clientes</Typography>
        </Breadcrumbs>

        {/* Header */}
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, justifyContent: 'space-between', mb: 4, gap: 2 }}>
          <Box>
            <Typography
              variant="h3"
              sx={{ color: '#0e141b', fontSize: { xs: '1.875rem', md: '2.25rem' }, fontWeight: 900, mb: 1, letterSpacing: '-0.033em' }}
            >
              {config.title}
            </Typography>
            <Typography sx={{ color: '#4e7397', fontSize: '1rem' }}>{config.subtitle}</Typography>
          </Box>
          <Button
            variant="outlined"
            onClick={() => navigate('/clients')}
            sx={{
              borderColor: '#800020', color: '#800020', alignSelf: { xs: 'stretch', md: 'center' },
              '&:hover': { borderColor: '#600010', bgcolor: 'rgba(128,0,32,0.04)' },
              borderRadius: '0.5rem', px: 3, py: 1.25, fontWeight: 700, textTransform: 'none', whiteSpace: 'nowrap',
            }}
          >
            Gestionar Clientes
          </Button>
        </Box>

        {/* Filters Info */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography sx={{ color: '#4e7397', fontSize: '0.875rem' }}>
            <span style={{ fontWeight: 700, color: '#0e141b' }}>{totalCount}</span> clientes {tagLabel.toLowerCase()}
          </Typography>
        </Box>

        {/* Search */}
        <Box sx={{ mb: 3 }}>
          <TextField
            placeholder="Buscar cliente..."
            value={searchTerm}
            onChange={(e) => { setSearchTerm(e.target.value); setPage(0); }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start"><SearchIcon sx={{ color: '#4e7397' }} /></InputAdornment>
              ),
              endAdornment: searchTerm && (
                <InputAdornment position="end">
                  <IconButton aria-label="clear search" onClick={() => { setSearchTerm(''); setPage(0); }} edge="end" size="small">
                    <ClearIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{
              width: '100%', maxWidth: '400px',
              '& .MuiOutlinedInput-root': {
                borderRadius: '0.5rem', backgroundColor: '#f6f7f8',
                '& fieldset': { borderColor: '#d0dbe7' },
                '&:hover fieldset': { borderColor: '#800020' },
                '&.Mui-focused fieldset': { borderColor: '#800020' },
              },
            }}
          />
        </Box>

        {/* Desktop Table */}
        <TableContainer
          component={Paper}
          sx={{ display: { xs: 'none', md: 'block' }, borderRadius: '0.75rem', border: '1px solid #d0dbe7', boxShadow: 'none', overflow: 'hidden' }}
        >
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: '#f6f7f8' }}>
                <TableCell sx={{ color: '#4e7397', fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase', py: 2 }}>Código</TableCell>
                <TableCell sx={{ color: '#4e7397', fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase' }}>Cliente / Razón Social</TableCell>
                <TableCell sx={{ color: '#4e7397', fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase' }}>CUIT</TableCell>
                <TableCell sx={{ color: '#4e7397', fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase' }}>Contacto / Email</TableCell>
                <TableCell sx={{ color: '#4e7397', fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase' }}>Dirección / Ciudad</TableCell>
                <TableCell sx={{ color: '#4e7397', fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase' }}>Lista</TableCell>
                <TableCell sx={{ color: '#4e7397', fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase' }}>Estado Crédito</TableCell>
                <TableCell sx={{ color: '#4e7397', fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase' }}>Saldo</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={8} align="center" sx={{ py: 4 }}>Cargando...</TableCell></TableRow>
              ) : clients.length === 0 ? (
                <TableRow><TableCell colSpan={8} align="center" sx={{ py: 4 }}>No hay clientes registrados en esta sección.</TableCell></TableRow>
              ) : (
                clients.map((client) => {
                  const statusColor = getStatusColor(client.credit_status);
                  return (
                    <TableRow key={client.id} sx={{ '&:hover': { bgcolor: '#f6f7f8' } }}>
                      <TableCell sx={{ fontWeight: 700, color: '#0e141b', fontSize: '0.8125rem' }}>
                        {client.cod_cliente || `PG-${client.id}`}
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <Avatar sx={{ bgcolor: '#800020', width: 32, height: 32, fontSize: '0.75rem', fontWeight: 700 }}>
                            {getInitials(client.name)}
                          </Avatar>
                          <Typography sx={{ color: '#0e141b', fontWeight: 700, fontSize: '0.875rem' }}>
                            {client.name}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell sx={{ color: '#0e141b', fontSize: '0.8125rem', fontFamily: 'monospace' }}>
                        {client.cuit}
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.8125rem' }}>
                        <Typography sx={{ fontSize: '0.8125rem', fontWeight: 600 }}>{client.contact_person || 'N/A'}</Typography>
                        <Typography sx={{ fontSize: '0.75rem', color: '#4e7397' }}>{client.email}</Typography>
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.8125rem' }}>
                        <Typography sx={{ fontSize: '0.8125rem', fontWeight: 500 }}>{client.delivery_address}</Typography>
                        <Typography sx={{ fontSize: '0.75rem', color: '#4e7397' }}>{client.city}</Typography>
                      </TableCell>
                      <TableCell>
                        <Chip label={getPriceListLabel(client.price_list)} size="small" sx={{ bgcolor: '#f6f7f8', fontSize: '0.7rem', fontWeight: 700 }} />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={getStatusLabel(client.credit_status)}
                          size="small"
                          sx={{
                            bgcolor: statusColor.bg,
                            color: statusColor.color,
                            border: `1px solid ${statusColor.border}`,
                            fontWeight: 700,
                            fontSize: '0.7rem'
                          }}
                        />
                      </TableCell>
                      <TableCell sx={{ fontWeight: 800, color: client.current_balance > 0 ? '#d32f2f' : '#2e7d32', fontSize: '0.8125rem' }}>
                        ${parseFloat(client.current_balance || 0).toLocaleString('es-AR')}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Mobile View */}
        <Box sx={{ display: { xs: 'flex', md: 'none' }, flexDirection: 'column', gap: 2 }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
          ) : clients.length === 0 ? (
            <Paper sx={{ p: 4, textAlign: 'center', borderRadius: '1rem', border: '1px solid #d0dbe7' }}>
              <Typography sx={{ color: '#4e7397' }}>No se encontraron clientes</Typography>
            </Paper>
          ) : (
            clients.map((client) => {
              const statusColor = getStatusColor(client.credit_status);
              const isBlocked = client.credit_status === 'bloqueado' || !client.is_active;
              return (
                <Paper
                  key={client.id}
                  sx={{
                    p: 2,
                    borderRadius: '1rem',
                    border: '1px solid #d0dbe7',
                    borderLeft: isBlocked ? '6px solid #d32f2f' : '1px solid #d0dbe7',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                    <Avatar sx={{ bgcolor: '#800020', width: 40, height: 40, fontSize: '0.875rem', fontWeight: 700 }}>
                      {getInitials(client.name)}
                    </Avatar>
                    <Box>
                      <Typography sx={{ fontWeight: 800, color: '#0e141b', fontSize: '0.95rem', lineHeight: 1.2 }}>{client.name}</Typography>
                      <Typography sx={{ color: '#4e7397', fontSize: '0.75rem' }}>{client.city} • CUIT: {client.cuit}</Typography>
                    </Box>
                  </Box>

                  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 2, p: 1.5, bgcolor: '#f6f7f8', borderRadius: '0.75rem' }}>
                    <Box>
                      <Typography sx={{ fontSize: '0.65rem', color: '#4e7397', textTransform: 'uppercase', fontWeight: 700 }}>Saldo</Typography>
                      <Typography sx={{ fontWeight: 900, color: client.current_balance > 0 ? '#d32f2f' : '#2e7d32', fontSize: '1rem' }}>
                        ${parseFloat(client.current_balance || 0).toLocaleString('es-AR')}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography sx={{ fontSize: '0.65rem', color: '#4e7397', textTransform: 'uppercase', fontWeight: 700 }}>Crédito</Typography>
                      <Typography sx={{ fontWeight: 700, fontSize: '0.875rem', color: statusColor.color, mt: 0.5 }}>
                        {getStatusLabel(client.credit_status)}
                      </Typography>
                    </Box>
                  </Box>

                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Chip label={getPriceListLabel(client.price_list)} size="small" sx={{ bgcolor: 'white', border: '1px solid #d0dbe7', fontWeight: 700, fontSize: '0.7rem' }} />
                    <Chip label={tagLabel} size="small" sx={{ bgcolor: 'rgba(0, 0, 0, 0.05)', fontWeight: 700, fontSize: '0.7rem' }} />
                  </Box>
                </Paper>
              );
            })
          )}
        </Box>

        {/* Pagination */}
        {totalCount > 0 && (
          <Pagination
            count={totalCount}
            page={page}
            rowsPerPage={20}
            onPageChange={(e, newPage) => setPage(newPage)}
          />
        )}
      </Box>
    </Box>
  );
};

export default SectionClients;
